import React, {
  createContext,
  useContext,
  useRef,
  useState,
  lazy,
  Suspense,
} from 'react'

import { ModalTop } from '../../../components/ModalTop'
import ConfirmExitModal from './ConfirmExitModal/ConfirmExitModal'
import useSeatmapStore from '../../../../../stores/seatmapStore'

import './SeatmapModal/seatmapModal.scss'

const SeatmapWrapper = lazy(() => import('./SeatmapWrapper/SeatmapWrapper'))

const SeatmapContext = createContext(undefined)

export const useSeatmap = () => {
  const context = useContext(SeatmapContext)
  if (!context) {
    throw new Error('Context must be used within a SeatmapProvider')
  }
  return context
}

/**
 * Seatmap open/close for fullscreen expand.
 * Closing with selected seats prompts for confirmation and wipes selection
 * (parity with the previous dialog + ConfirmExitModal flow).
 */
export const SeatmapProvider = ({ children }) => {
  const selectedFromMap = useSeatmapStore((state) => state.selectedFromMap)
  const resetMapSelection = useSeatmapStore((state) => state.resetMapSelection)

  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const confirmDialogRef = useRef(null)

  const showSeatmap = () => setFullscreenOpen(true)

  const hideSeatmap = () => {
    if (selectedFromMap?.length > 0) {
      confirmDialogRef.current?.showModal()
      return
    }
    confirmCloseMap()
  }

  const confirmCloseMap = () => {
    confirmDialogRef.current?.close()
    setFullscreenOpen(false)
    resetMapSelection()
  }

  const cancelCloseMap = () => {
    confirmDialogRef.current?.close()
  }

  return (
    <SeatmapContext.Provider
      value={{
        showSeatmap,
        hideSeatmap,
        fullscreenOpen,
        SeatmapWrapper,
      }}
    >
      {children}
      {fullscreenOpen ? (
        <div
          className="seatmap-fullscreen-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Select your seats"
        >
          <ModalTop
            title="Select your seats"
            handleClose={hideSeatmap}
            isSeatmapModal
          />
          <div className="seatmap-fullscreen-overlay__map">
            <Suspense fallback={null}>
              <SeatmapWrapper handleClose={hideSeatmap} />
            </Suspense>
          </div>
        </div>
      ) : null}
      <ConfirmExitModal
        dialogRef={confirmDialogRef}
        confirm={confirmCloseMap}
        cancel={cancelCloseMap}
      />
    </SeatmapContext.Provider>
  )
}
